// Invoices — list + detail + the appointment handoff. One invoice per
// appointment: created when the appointment is booked, kept in sync by the
// database while it's open, completed at checkout, voided when the
// appointment is cancelled, deleted or marked a no-show. Money is only ever
// taken at the appointment's checkout — this screen shows what's owed and
// "Open appointment" opens the existing appointment details (the same panel
// Calendar and Bookings use) over the invoice; checkout, no-show and cancel
// all happen there, and closing it lands back on the invoice, refreshed.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { supabase } from '../../utils/supabase'
import { fetchInvoices, buildInvoiceView, splitByStatus, invoiceTotals, searchInvoices } from '../../utils/invoiceUtils'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { fetchPetAvatarsBatch } from '../../utils/petPhotoUtils'
import { fetchMerchantLogo, getMerchantPhotoUrl } from '../../utils/merchantPhotoUtils'
import AppointmentDetailsDialog from '../AppointmentDetailsDialog'
import InvoicesList from './InvoicesList'
import InvoiceDetail from './InvoiceDetail'
import { T, ensureOutfitFont } from './invoicesUi'

// `openInvoiceId` (a payments row id) opens that invoice's detail as soon as
// the list loads — used by a completed appointment's "View invoice" action.
// While `onBackToOrigin` is set, that invoice's back button returns to the
// appointment it came from instead of the Invoices list.
export default function InvoicesManager({ merchant, openInvoiceId = null, onOpenConsumed, onBackToOrigin = null }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const [rows, setRows] = useState(null)
	const [error, setError] = useState(null)
	const [tab, setTab] = useState('unpaid')
	const [query, setQuery] = useState('')
	const [selectedId, setSelectedId] = useState(null)
	// The appointment row shown in the details dialog over the invoice.
	const [openAppt, setOpenAppt] = useState(null)
	// Invoice that was still open (unpaid/upcoming) when its appointment was
	// opened — if it comes back paid, the detail shows the "just paid" banner.
	const awaitingPaidRef = useRef(null)
	const [justPaidId, setJustPaidId] = useState(null)
	const [downloadingId, setDownloadingId] = useState(null)
	const [petAvatars, setPetAvatars] = useState({})
	const [logoUrl, setLogoUrl] = useState(null)

	// The store logo for the invoice document header (initials badge without).
	useEffect(() => {
		if (!merchant?.id) return
		let cancelled = false
		fetchMerchantLogo(merchant.id).then((photo) => {
			if (!cancelled) setLogoUrl(photo ? getMerchantPhotoUrl(photo) : null)
		})
		return () => { cancelled = true }
	}, [merchant?.id])
	// True while the selected invoice is the one opened from an appointment.
	const [fromOrigin, setFromOrigin] = useState(false)

	useEffect(() => { ensureOutfitFont() }, [])

	const load = useCallback(async () => {
		if (!merchant?.id) return
		setError(null)
		try {
			setRows(await fetchInvoices(merchant.id))
		} catch (e) {
			console.error('Error loading invoices:', e)
			setError(e)
		}
	}, [merchant?.id])

	useEffect(() => { load() }, [load])

	const views = useMemo(
		() => (rows || []).map((r) => buildInvoiceView(r, merchant)),
		[rows, merchant]
	)
	const byStatus = useMemo(() => splitByStatus(views), [views])

	// Pet avatars for the invoice documents, looked up by the snapshot's pet_id.
	useEffect(() => {
		const petIds = [...new Set(views.flatMap((v) => v.pets.map((p) => p.petId)).filter(Boolean))]
		if (petIds.length === 0) return
		let cancelled = false
		fetchPetAvatarsBatch(petIds).then((map) => { if (!cancelled) setPetAvatars(map || {}) })
		return () => { cancelled = true }
	}, [views])
	const money = useCallback((cents) => {
		const cs = getCurrencySymbol(merchant?.currency)
		return `${cs}${((cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
	}, [merchant?.currency])
	const totals = useMemo(() => invoiceTotals(byStatus, money), [byStatus, money])

	// Deep link from an appointment: select the invoice once it's in the list.
	useEffect(() => {
		if (!openInvoiceId || !rows) return
		const view = views.find((v) => v.id === openInvoiceId)
		if (view) {
			setJustPaidId(null)
			setSelectedId(view.id)
			setTab(view.status)
			setFromOrigin(true)
		}
		onOpenConsumed?.()
	}, [openInvoiceId, rows, views, onOpenConsumed])

	const backFromDetail = () => {
		setSelectedId(null)
		setJustPaidId(null)
		if (fromOrigin) {
			setFromOrigin(false)
			onBackToOrigin?.()
		}
	}
	const backLabel = fromOrigin && onBackToOrigin ? 'Back to appointment' : 'Back to invoices'

	const searching = query.trim().length > 0
	const listRows = searching ? searchInvoices(views, query) : byStatus[tab]
	const selected = selectedId ? views.find((v) => v.id === selectedId) : null

	// "Open appointment" — load the row in the shape the details dialog expects
	// (same columns + joins as the Bookings list) and open it over the invoice.
	const openAppointment = useCallback(async (inv) => {
		if (!inv?.appointmentId) return
		const { data, error: apptError } = await supabase
			.from('appointments')
			.select(`
				*,
				service_types(id, name, duration_minutes, price, appointment_color),
				pets(id, name, breed),
				employees(id, first_name, last_name)
			`)
			.eq('id', inv.appointmentId)
			.single()
		if (apptError || !data) {
			console.error('Error loading appointment for invoice:', apptError)
			return
		}
		awaitingPaidRef.current = inv.canCheckout ? inv.id : null
		setSelectedId(inv.id)
		setJustPaidId(null)
		setOpenAppt(data)
	}, [])

	// Back from the appointment: refresh so the invoice reflects whatever
	// happened there (paid, voided, edited).
	const closeAppointment = useCallback(() => {
		setOpenAppt(null)
		load()
	}, [load])

	// Paid at the appointment's checkout → the invoice comes back Paid.
	useEffect(() => {
		const id = awaitingPaidRef.current
		if (!id) return
		const view = views.find((v) => v.id === id)
		if (!view) return
		if (view.isPaid) {
			awaitingPaidRef.current = null
			setJustPaidId(id)
		} else if (!view.canCheckout) {
			awaitingPaidRef.current = null // voided instead
		}
	}, [views])

	// "View invoice" inside the appointment dialog: we're already on Invoices,
	// so just make sure that invoice is the selected one (the dialog closes
	// itself first, which already refreshes the list).
	const showInvoiceFromAppointment = useCallback((payment) => {
		setOpenAppt(null)
		if (payment?.id) setSelectedId(payment.id)
	}, [])

	// "Download PDF" — the renderer is heavy, so it's fetched on first use.
	const handleDownload = useCallback(async (inv) => {
		setDownloadingId(inv.id)
		try {
			const { downloadInvoicePdf } = await import('./InvoicePdf')
			await downloadInvoicePdf(inv, merchant, petAvatars, logoUrl)
		} catch (e) {
			console.error('Error generating invoice PDF:', e)
		} finally {
			setDownloadingId(null)
		}
	}, [merchant, petAvatars, logoUrl])

	return (
		<Box sx={{ minHeight: '100%', bgcolor: T.bg, fontFamily: T.font, WebkitFontSmoothing: 'antialiased', p: isMobile ? 0 : '22px 26px 30px' }}>
			{selected ? (
				<InvoiceDetail
					inv={selected}
					merchant={merchant}
					isMobile={isMobile}
					justPaid={justPaidId === selected.id}
					onBack={backFromDetail}
					backLabel={backLabel}
					onOpenAppointment={openAppointment}
					onDownload={handleDownload}
					downloading={downloadingId === selected.id}
					petAvatars={petAvatars}
					logoUrl={logoUrl}
				/>
			) : (
				<InvoicesList
					isMobile={isMobile}
					loading={rows === null && !error}
					error={error}
					onRetry={load}
					views={views}
					byStatus={byStatus}
					totals={totals}
					tab={tab}
					onTab={setTab}
					query={query}
					onQuery={setQuery}
					rows={listRows}
					searching={searching}
					onOpen={(inv) => { setJustPaidId(null); setFromOrigin(false); setSelectedId(inv.id) }}
					onOpenAppointment={openAppointment}
					onDownload={handleDownload}
					downloadingId={downloadingId}
				/>
			)}

			{openAppt && (
				<AppointmentDetailsDialog
					open
					onClose={closeAppointment}
					appointment={openAppt}
					merchant={merchant}
					onAppointmentUpdated={load}
					onOpenInvoice={showInvoiceFromAppointment}
				/>
			)}
		</Box>
	)
}
